import type { Instance } from "mobx-state-tree";
import { flow, types } from "mobx-state-tree";
import * as Sentry from "@sentry/react";

import type { ProjectStatus } from "@/domains/projects/status/projectStatus";
import { onProjectStatusRecovery } from "@/domains/projects/status/projectStatusAvailability";
import { safeErrorCode } from "@/domains/projects/status/safeErrorCode";
import { logger } from "@/utils/logger";

import * as repo from "./repository";

const ProjectOptionModel = types.model("ProjectOption", {
  id: types.number,
  name: types.string,
  status: types.optional(types.enumeration("ProjectStatus", ["active", "inactive"]), "active"),
});

const FolderOptionModel = types.model("FolderOption", {
  id: types.number,
  folder_path: types.string,
});

const projectKey = (orgId: string, buildingId: number) => `${orgId}|${buildingId}`;

const folderKey = (orgId: string, buildingId: number, projectId: number) =>
  `${orgId}|${buildingId}|${projectId}`;

// Monotonic per-key load tokens. A
// newer load for a (org, building) supersedes an in-flight older one, so a slow
// initial fetch can't clobber the reload fired right after creating a project
// (which is what dropped a just-created project from the header selector).
export const ProjectFolderStore = types
  .model("ProjectFolderStore", {
    projectOptionsByBuilding: types.map(types.array(ProjectOptionModel)),
    folderOptionsByProject: types.map(types.array(FolderOptionModel)),
    isLoadingProjects: types.optional(types.boolean, false),
    isLoadingFolders: types.optional(types.boolean, false),
    projectsError: types.maybeNull(types.string),
    foldersError: types.maybeNull(types.string),
  })
  .volatile(() => ({
    projectLoadSeq: new Map<string, number>(),
    pendingProjectKeys: new Set<string>(),
    unsubscribeStatusRecovery: null as (() => void) | null,
  }))
  .views((self) => ({
    getProjectOptions(orgId: string, buildingId: number) {
      return self.projectOptionsByBuilding.get(projectKey(orgId, buildingId)) ?? [];
    },
    hasProjectOptions(orgId: string, buildingId: number) {
      return self.projectOptionsByBuilding.has(projectKey(orgId, buildingId));
    },
    getFolderOptions(orgId: string, buildingId: number, projectId: number) {
      return self.folderOptionsByProject.get(folderKey(orgId, buildingId, projectId)) ?? [];
    },
    hasFolderOptions(orgId: string, buildingId: number, projectId: number) {
      return self.folderOptionsByProject.has(folderKey(orgId, buildingId, projectId));
    },
  }))
  .actions((self) => ({
    loadProjectOptions: flow(function* loadProjectOptions(orgId: string, buildingId: number) {
      const key = projectKey(orgId, buildingId);
      const seq = (self.projectLoadSeq.get(key) ?? 0) + 1;
      self.projectLoadSeq.set(key, seq);
      self.pendingProjectKeys.add(key);
      self.isLoadingProjects = true;
      self.projectsError = null;
      try {
        const rows: repo.ProjectOption[] = yield repo.fetchProjectOptions({
          orgId,
          buildingId,
        });
        // A newer load for this key started while we were awaiting: discard this
        // (older) result so it can't overwrite the fresher project list.
        if (self.projectLoadSeq.get(key) !== seq) return;
        self.projectOptionsByBuilding.set(
          key,
          rows.map((r) => ProjectOptionModel.create(r)) as never
        );
      } catch (e: unknown) {
        if (self.projectLoadSeq.get(key) === seq) {
          self.projectsError = e instanceof Error ? e.message : String(e);
        }
        throw e;
      } finally {
        if (self.projectLoadSeq.get(key) === seq) {
          self.pendingProjectKeys.delete(key);
          self.isLoadingProjects = self.pendingProjectKeys.size > 0;
        }
      }
    }),

    loadFolderOptions: flow(function* loadFolderOptions(
      orgId: string,
      buildingId: number,
      projectId: number
    ) {
      self.isLoadingFolders = true;
      self.foldersError = null;
      try {
        const rows: repo.FolderOption[] = yield repo.fetchFolderOptions({
          orgId,
          buildingId,
          projectId,
        });
        self.folderOptionsByProject.set(
          folderKey(orgId, buildingId, projectId),
          rows.map((r) => FolderOptionModel.create(r)) as never
        );
      } catch (e: unknown) {
        self.foldersError = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoadingFolders = false;
      }
    }),

    /**
     * Insert a just-created project into the cached options so scoping to it
     * is valid immediately — the async reload that follows a create may still
     * be in flight (or fail) when the picker scopes to the new id, and an id
     * missing from the options gets stripped by useBuildingScope's reconcile.
     * No-op when the options for this (org, building) were never loaded: a
     * one-row cache entry would flip `hasProjectOptions` true and masquerade
     * as the full list.
     */
    addProjectOption(
      orgId: string,
      buildingId: number,
      option: { id: number; name: string; status?: ProjectStatus }
    ) {
      const existing = self.projectOptionsByBuilding.get(projectKey(orgId, buildingId));
      if (!existing || existing.some((p) => p.id === option.id)) return;
      existing.push(ProjectOptionModel.create(option));
    },

    invalidateFolderOptions(orgId: string, buildingId: number, projectId: number) {
      self.folderOptionsByProject.delete(folderKey(orgId, buildingId, projectId));
    },

    invalidateProjectOptions(orgId: string, buildingId: number) {
      self.projectOptionsByBuilding.delete(projectKey(orgId, buildingId));
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.unsubscribeStatusRecovery = onProjectStatusRecovery(() => {
        const keys = new Set([...self.projectOptionsByBuilding.keys(), ...self.pendingProjectKeys]);
        for (const key of keys) {
          const separator = key.lastIndexOf("|");
          const orgId = key.slice(0, separator);
          const buildingId = Number(key.slice(separator + 1));
          void self.loadProjectOptions(orgId, buildingId).catch((error: unknown) => {
            const errorCode = safeErrorCode(error);
            const reportableError = new Error(
              `Project status option recovery reload failed (${errorCode})`
            );
            let sentryEventId: string | undefined;
            try {
              sentryEventId = Sentry.captureException(reportableError, {
                tags: {
                  area: "projects",
                  operation: "project_status_options_recovery",
                  error_code: errorCode,
                },
                contexts: {
                  project_status_options_recovery: {
                    organization_id: orgId,
                    building_id: buildingId,
                  },
                },
                fingerprint: ["projects", "project-status", "options-recovery"],
              });
            } catch {
              // Reporting failures must not remove the cached picker options.
            }
            void logger
              .error(
                "database",
                "project_status_options_recovery",
                "Failed to refresh project status options after recovery",
                reportableError,
                {
                  organization_id: orgId,
                  building_id: buildingId,
                  error_code: errorCode,
                  sentry_event_id: sentryEventId,
                }
              )
              .catch(() => {
                // Log persistence is independent of the cached picker options.
              });
          });
        }
      });
    },
    beforeDestroy() {
      self.unsubscribeStatusRecovery?.();
    },
  }));

export type ProjectFolderStoreInstance = Instance<typeof ProjectFolderStore>;
